import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  Grid,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTranslation } from '../../hooks/useTranslation';
import SimulationsTable from '../SimulationsTable';
import api from '../../services/api'

const HistoricalSimulation = ({ client_id, isSecondDesign = false, bare = false }) => {

  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [simulations, setSimulations] = useState([]);
  const [filteredSimulations, setFilteredSimulations] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    revenda: 0,
    redutoCapital: 0,
    pendentes: 0,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const paginatedSimulations = (filteredSimulations ?? []).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const fetchSimulation = useCallback(async () => {
    try {
      setLoading(true);
      const { data: resultado } = await api.get(`/v1/simulation?customer_id=${client_id}`);
      setSimulations(resultado.list ?? []);
      setFilteredSimulations(resultado.list ?? []);
      setStats({
        total: resultado.overview.total,
        revenda: resultado.overview.revenda,
        redutoCapital: resultado.overview.reduto_capital,
        pendentes: resultado.overview.pending,
      });
    } catch (error) {
      console.error(t('messages.errorFetchingData'), error);
    } finally {
      setLoading(false);
    }
  }, [client_id]);

  useEffect(() => {
    if (client_id) {
      setSimulations([]);
      setFilteredSimulations([]);
      setSearchTerm('');
      setFiltroTipo('todos');
      setFiltroStatus('todos');

      fetchSimulation();
    } else {
      setSimulations([]);
      setFilteredSimulations([]);
    }
  }, [client_id, fetchSimulation]);

  useEffect(() => {
    let filtered = simulations ?? [];

    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(
        (sim) =>
          sim.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sim.clientCompany.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filtroTipo !== 'todos') {
      filtered = filtered.filter((sim) => sim.type === filtroTipo);
    }

    if (filtroStatus !== 'todos') {
      filtered = filtered.filter((sim) => sim.stage === filtroStatus);
    }

    setFilteredSimulations(filtered);
  }, [searchTerm, filtroTipo, filtroStatus, simulations]);


  const Container = bare ? Box : Paper;
  const containerSx = bare
    ? { width: '100%' }
    : {
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: 2,
        border: 1,
        borderColor: '#E5E7EB',
        bgcolor: '#fff',
        width: '100%',
      };

  if (!client_id) {
    return (
      <Container sx={containerSx}>
        <Box
          sx={{
            textAlign: 'center',
            py: 12,
            bgcolor: '#FAFAFA',
            borderRadius: 2,
            border: 1,
            borderColor: '#E5E7EB',
          }}
        >
          <PersonSearchIcon sx={{ fontSize: 64, color: '#9CA3AF', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#6B7280', mb: 1, fontWeight: 600 }}>
            {t('simulation.selectClientToViewHistory')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
            {t('simulation.selectClientAboveToSeeSimulations')}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={containerSx}>
      {/* Stats Cards */}
      {!isSecondDesign && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: '#FFFFFF',
                boxShadow: 1,
                borderRadius: '12px',
                border: '1px solid #E1E7EF',
                padding: '14px',
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: '#757575', mb: 0.5, fontSize: { xs: '12px', md: '16px' } }}
                >
                  {t('simulation.total')}
                </Typography>
                <Typography variant="h4" sx={{ color: '#2C2C2C', fontWeight: 700, fontSize: '24px' }}>
                  {stats.total}
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: '#E7F0FE',
                boxShadow: 1,
                borderRadius: '12px',
                border: '1px solid #3C83F633',
                padding: '14px',
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: '#3C83F6', mb: 0.5, fontSize: { xs: '12px', md: '16px' } }}
                >
                  {t('simulation.resale')}
                </Typography>
                <Typography variant="h4" sx={{ color: '#3C83F6', fontWeight: 700, fontSize: '24px' }}>
                  {stats.revenda}
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: '#EDFFE2',
                boxShadow: 1,
                borderRadius: '12px',
                border: '1px solid #83D84D',
                padding: '14px',
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: '#166534', mb: 0.5, fontSize: { xs: '12px', md: '16px' } }}
                >
                  Reduto Capital
                </Typography>
                <Typography variant="h4" sx={{ color: '#166534', fontWeight: 700, fontSize: '24px' }}>
                  {stats.redutoCapital}
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card
              sx={{
                bgcolor: '#FEF8E6',
                boxShadow: 1,
                borderRadius: '12px',
                border: '1px solid #854D0E33',
                padding: '14px',
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: '#854D0E', mb: 0.5, fontSize: { xs: '12px', md: '16px' } }}
                >
                  {t('simulation.pending')}
                </Typography>
                <Typography variant="h4" sx={{ color: '#E7B008', fontWeight: 700, fontSize: '24px' }}>
                  {stats.pendentes}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search and Filters */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: isSecondDesign ? 'space-between' : 'right'
        }}
      >
        {isSecondDesign && (
          <Typography 
            sx={{
              color: '#2C2C2C',
              fontSize: '16px',
              fontWeight: '700'
            }}
          >
            {t('simulation.simulationHistory')}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <FormControl sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <Select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <FilterListIcon sx={{ color: '#9CA3AF', ml: 1 }} />
                </InputAdornment>
              }
              sx={{
                borderRadius: '10px',
                border: '1px solid #E1E7EF',
                bgcolor: '#F9FAFB',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E5E7EB',
                },
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
              }}
            >
              <MenuItem value="todos">{t('simulation.allTypes')}</MenuItem>
              <MenuItem value="REVENDA">{t('simulation.resale')}</MenuItem>
              <MenuItem value="REDUTO_CAPITAL">Reduto Capital</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <Select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: '10px',
                border: '1px solid #E1E7EF',
                bgcolor: '#F9FAFB',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E5E7EB',
                },
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
              }}
            >
              <MenuItem value="todos">{t('simulation.allStatus')}</MenuItem>
              <MenuItem value="APPROVED">{t('simulation.approved')}</MenuItem>
              <MenuItem value="PENDING">{t('simulation.pending')}</MenuItem>
              <MenuItem value="REJECTED">{t('simulation.failed')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Table */}
      <SimulationsTable
        simulations={simulations}
        isSecondDesign={isSecondDesign}    
      />

      {/* Empty State */}
      {!loading && filteredSimulations.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            bgcolor: '#FFFFFF',
            borderRadius: 2,
            mt: 2,
            border: 1,
            borderColor: '#E5E7EB',
          }}
        >
          <Typography variant="h6" sx={{ color: '#6B7280', mb: 1 }}>
            {t('simulation.noSimulationFound')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
            {t('simulation.tryAdjustingYourSearch')}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default HistoricalSimulation;
